// Drum kit and bass voice synthesised with Web Audio, so the backing track
// needs no sample files.

function noiseBuffer(ctx) {
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
}

function envelope(ctx, output, time, peak, attack, decay) {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), time + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + attack + decay);
    gain.connect(output);
    return gain;
}

export class DrumKit {
    constructor(ctx, output) {
        this.ctx = ctx;
        this.output = output;
        this.noise = noiseBuffer(ctx);
        this.openHat = null;
    }

    play(voice, time, velocity) {
        if (this[voice]) this[voice](time, velocity);
    }

    noiseSource(time, duration) {
        const source = this.ctx.createBufferSource();
        source.buffer = this.noise;
        source.start(time, Math.random() * 0.5);
        source.stop(time + duration);
        return source;
    }

    filter(type, frequency, q = 1) {
        const filter = this.ctx.createBiquadFilter();
        filter.type = type;
        filter.frequency.value = frequency;
        filter.Q.value = q;
        return filter;
    }

    kick(time, velocity) {
        const { ctx } = this;
        const osc = ctx.createOscillator();
        osc.frequency.setValueAtTime(160, time);
        osc.frequency.exponentialRampToValueAtTime(48, time + 0.12);
        osc.connect(envelope(ctx, this.output, time, velocity, 0.002, 0.42));
        osc.start(time);
        osc.stop(time + 0.5);

        const click = this.noiseSource(time, 0.02);
        click.connect(this.filter('highpass', 2500)).connect(envelope(ctx, this.output, time, velocity * 0.25, 0.001, 0.012));
    }

    snare(time, velocity) {
        const { ctx } = this;
        const noise = this.noiseSource(time, 0.25);
        noise.connect(this.filter('highpass', 1400)).connect(envelope(ctx, this.output, time, velocity * 0.55, 0.001, 0.18));

        const body = ctx.createOscillator();
        body.type = 'triangle';
        body.frequency.setValueAtTime(220, time);
        body.frequency.exponentialRampToValueAtTime(170, time + 0.08);
        body.connect(envelope(ctx, this.output, time, velocity * 0.45, 0.001, 0.09));
        body.start(time);
        body.stop(time + 0.12);
    }

    rim(time, velocity) {
        const { ctx } = this;
        const osc = ctx.createOscillator();
        osc.type = 'square';
        osc.frequency.value = 1750;
        osc.connect(this.filter('bandpass', 1750, 4)).connect(envelope(ctx, this.output, time, velocity * 0.5, 0.0005, 0.03));
        osc.start(time);
        osc.stop(time + 0.05);
        const click = this.noiseSource(time, 0.02);
        click.connect(this.filter('bandpass', 4000, 1)).connect(envelope(ctx, this.output, time, velocity * 0.25, 0.0005, 0.01));
    }

    hat(time, velocity) {
        this.chokeOpenHat(time);
        const noise = this.noiseSource(time, 0.1);
        noise.connect(this.filter('highpass', 7000)).connect(this.filter('peaking', 10000, 1))
            .connect(envelope(this.ctx, this.output, time, velocity * 0.22, 0.001, 0.045));
    }

    open(time, velocity) {
        this.chokeOpenHat(time);
        const noise = this.noiseSource(time, 0.45);
        const gain = envelope(this.ctx, this.output, time, velocity * 0.2, 0.002, 0.35);
        noise.connect(this.filter('highpass', 6500)).connect(gain);
        this.openHat = { gain, end: time + 0.35 };
    }

    chokeOpenHat(time) {
        if (this.openHat && this.openHat.end > time) {
            this.openHat.gain.gain.cancelScheduledValues(time);
            this.openHat.gain.gain.setTargetAtTime(0.0001, time, 0.01);
        }
        this.openHat = null;
    }

    shaker(time, velocity) {
        const noise = this.noiseSource(time, 0.12);
        noise.connect(this.filter('bandpass', 6000, 1.2)).connect(envelope(this.ctx, this.output, time, velocity * 0.18, 0.012, 0.06));
    }
}

const midiHz = midi => 440 * Math.pow(2, (midi - 69) / 12);

// Saw plus a sine an octave down through a plucky low-pass: round enough to
// sit under a guitar, with enough upper harmonics to be heard on laptop
// speakers that can't reproduce the fundamental.
export class BassVoice {
    constructor(ctx, output) {
        this.ctx = ctx;
        this.output = output;
    }

    // Returns a handle that retune() and release() can change later.
    note(midi, time, duration, velocity) {
        const { ctx } = this;
        const saw = ctx.createOscillator();
        saw.type = 'sawtooth';
        const sub = ctx.createOscillator();
        sub.type = 'sine';
        const freq = midiHz(midi);
        saw.frequency.setValueAtTime(freq, time);
        sub.frequency.setValueAtTime(freq, time);

        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.Q.value = 3;
        filter.frequency.setValueAtTime(1100, time);
        filter.frequency.exponentialRampToValueAtTime(380, time + 0.18);

        const sawGain = ctx.createGain();
        sawGain.gain.value = 0.55;
        const gain = ctx.createGain();
        const peak = 0.6 * velocity;
        gain.gain.setValueAtTime(0.0001, time);
        gain.gain.exponentialRampToValueAtTime(peak, time + 0.006);
        gain.gain.exponentialRampToValueAtTime(peak * 0.6, time + 0.25);
        const end = time + duration;
        gain.gain.setValueAtTime(peak * 0.6, Math.max(time + 0.25, end - 0.04));
        gain.gain.exponentialRampToValueAtTime(0.0001, end);

        saw.connect(sawGain).connect(filter);
        sub.connect(filter);
        filter.connect(gain).connect(this.output);
        saw.start(time);
        sub.start(time);
        saw.stop(end + 0.05);
        sub.stop(end + 0.05);
        return { saw, sub, gain, filter, start: time, end, midi, instrument: this };
    }

    // Slide a playing (or scheduled) note to a new pitch.
    retune(voice, midi, time) {
        const freq = midiHz(midi);
        [voice.saw, voice.sub].forEach(osc => {
            osc.frequency.cancelScheduledValues(time);
            if (time <= voice.start) {
                osc.frequency.setValueAtTime(freq, voice.start);
            } else {
                osc.frequency.setTargetAtTime(freq, time, 0.012);
            }
        });
        voice.midi = midi;
    }

    release(voice, time) {
        if (voice.end <= time) return;
        if (time <= voice.start) {
            // Not started yet: drop it rather than unpicking its envelope.
            voice.saw.stop(voice.start);
            voice.sub.stop(voice.start);
        } else {
            const gain = voice.gain.gain;
            if (gain.cancelAndHoldAtTime) {
                gain.cancelAndHoldAtTime(time);
            } else {
                gain.cancelScheduledValues(time);
            }
            gain.setTargetAtTime(0.0001, time, 0.015);
        }
        voice.end = time;
    }
}

// The metronome's click: a short pitched tick over a burst of noise. It's
// pitched above 2 kHz, where neither the chord listener nor the tuner looks,
// so it can tick along while they listen through speakers.
const CLICKS = {
    accent: { frequency: 3136, level: 0.9 },
    beat: { frequency: 2349, level: 0.6 },
    sub: { frequency: 2349, level: 0.25 },
};

export class MetronomeClick {
    constructor(ctx, output) {
        this.ctx = ctx;
        this.output = output;
        this.noise = noiseBuffer(ctx);
    }

    play(time, kind = 'beat') {
        const { ctx } = this;
        const { frequency, level } = CLICKS[kind] || CLICKS.beat;
        const osc = ctx.createOscillator();
        osc.frequency.value = frequency;
        osc.connect(envelope(ctx, this.output, time, level * 0.5, 0.0005, 0.045));
        osc.start(time);
        osc.stop(time + 0.06);

        const tick = ctx.createBufferSource();
        tick.buffer = this.noise;
        const filter = ctx.createBiquadFilter();
        filter.type = 'highpass';
        filter.frequency.value = 3000;
        tick.connect(filter).connect(envelope(ctx, this.output, time, level * 0.3, 0.0005, 0.008));
        tick.start(time, Math.random() * 0.5);
        tick.stop(time + 0.02);
    }
}
